import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import RoleSelectionCard from './components/RoleSelectionCard';
import ProgressIndicator from './components/ProgressIndicator';
import OrganizationFields from './components/OrganizationFields';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import { USER_ROLES } from '../../utils/smartCitiesData';

const UserRegistration = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailValidated, setEmailValidated] = useState(false);
  const [authorizedRole, setAuthorizedRole] = useState(null);
  const [roleValidationLoading, setRoleValidationLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Basic Information (moved up)
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Step 2: Role Selection (moved after basic info)
    role: '',
    
    // Step 3: Organization Details
    organization: '',
    title: '',
    phone: '',
    location: '',
    
    // City Official specific fields
    city: '',
    department: '',
    
    // Student specific fields
    university: '',
    customUniversity: '',
    major: '',
    academicYear: '',
    expectedGraduation: '',
    
    // Step 4: Additional Info
    bio: '',
    interests: [],
    skills: [],
    linkedinUrl: '',
    websiteUrl: '',
    
    // Agreements
    termsAccepted: false,
    privacyAccepted: false,
    marketingConsent: false
  });

  const handleRoleSelect = async (roleId) => {
    setRoleValidationLoading(true);
    setErrors((prev) => ({ ...prev, roleValidation: null }));

    const cleanEmail = formData?.email?.trim()?.toLowerCase();

    if (!cleanEmail) {
      setErrors((prev) => ({
        ...prev,
        roleValidation: 'Please enter your email before selecting a role.',
      }));
      setRoleValidationLoading(false);
      return;
    }

    try {
      // Enhanced role validation with better error handling
      const { data, error: supaError } = await supabase
        ?.from('role_email_authorizations')
        ?.select('*')
        ?.eq('email', cleanEmail)
        ?.eq('role', roleId)
        ?.eq('is_active', true)
        ?.maybeSingle();

      if (supaError) {
        console.error('Supabase error:', supaError);
        setErrors((prev) => ({
          ...prev,
          roleValidation: 'Error verifying your role authorization. Please try again later.',
        }));
        setRoleValidationLoading(false);
        return;
      }

      if (!data) {
        setErrors((prev) => ({
          ...prev,
          roleValidation: `The email ${cleanEmail} is not authorized for the ${roleId?.replace('_', ' ')} role. Please contact your administrator.`,
        }));
        setRoleValidationLoading(false);
        return;
      }

      // If successful, update form data and proceed
      handleInputChange('role', roleId);
      setAuthorizedRole(roleId);
      setEmailValidated(true);
      setRoleValidationLoading(false);
      
      // Small delay to ensure state updates are processed
      setTimeout(() => {
        setCurrentStep(prev => Math.min(prev + 1, 4));
      }, 100);

    } catch (error) {
      console.error('Role selection error:', error);
      setErrors((prev) => ({
        ...prev,
        roleValidation: 'Error verifying your role authorization. Please try again later.',
      }));
      setRoleValidationLoading(false);
    }
  };

  const totalSteps = 4;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // If email changes, reset validation
    if (field === 'email') {
      setEmailValidated(false);
      setAuthorizedRole(null);
      if (formData?.role) {
        setFormData(prev => ({ ...prev, role: '' }));
      }
    }

    // If role changes on step 2, clear any role validation errors
    if (field === 'role' && currentStep === 2) {
      setErrors(prev => ({ ...prev, role: '', roleValidation: '' }));
    }
  };

  const validateEmailAuthorization = async () => {
    if (!formData?.email) {
      setErrors(prev => ({ ...prev, roleValidation: 'Email is required for validation' }));
      return false;
    }

    if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      setErrors(prev => ({ ...prev, roleValidation: 'Please enter a valid email address' }));
      return false;
    }

    if (!formData?.role) {
      setErrors(prev => ({ ...prev, roleValidation: 'Please select a role first' }));
      return false;
    }

    // Skip additional validation if already validated through role selection
    if (emailValidated && authorizedRole === formData?.role) {
      return true;
    }

    setRoleValidationLoading(true);
    
    try {
      // Direct database check since we can't rely on RPC functions
      const { data: authCheck, error: authError } = await supabase
        ?.from('role_email_authorizations')
        ?.select('*')
        ?.eq('email', formData?.email?.toLowerCase()?.trim())
        ?.eq('role', formData?.role)
        ?.eq('is_active', true)
        ?.maybeSingle();

      if (authError) {
        console.error('Email authorization check failed:', authError);
        setErrors(prev => ({ ...prev, roleValidation: 'Unable to verify email authorization. Please try again.' }));
        setRoleValidationLoading(false);
        return false;
      }

      if (!authCheck) {
        setErrors(prev => ({ 
          ...prev, 
          roleValidation: 'This email is not authorized for the selected role. Please contact your administrator.' 
        }));
        setRoleValidationLoading(false);
        return false;
      }

      setAuthorizedRole(formData?.role);
      setEmailValidated(true);
      setRoleValidationLoading(false);
      setErrors(prev => ({ ...prev, roleValidation: '' }));
      return true;
    } catch (error) {
      console.error('Email validation error:', error);
      setErrors(prev => ({ ...prev, roleValidation: 'Unable to verify email authorization. Please try again.' }));
      setRoleValidationLoading(false);
      return false;
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.fullName) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData?.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData?.password) {
        newErrors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (step === 2) {
      if (!formData?.role) {
        newErrors.role = 'Please select your role';
      }
      // Don't validate email authorization here - it will be done when clicking Next
    }

    if (step === 3) {
      // Role-based validation for organization step
      if (formData?.role === USER_ROLES?.STUDENT) {
        // For students, validate university, major, academicYear, and location
        if (!formData?.university) {
          newErrors.university = 'University is required';
        }
        if (formData?.university === 'other' && !formData?.customUniversity?.trim()) {
          newErrors.customUniversity = 'Please enter your university name';
        }
        if (!formData?.major) {
          newErrors.major = 'Major is required';
        }
        if (!formData?.academicYear) {
          newErrors.academicYear = 'Academic year is required';
        }
        if (!formData?.expectedGraduation) {
          newErrors.expectedGraduation = 'Expected graduation date is required';
        }
        if (!formData?.location) {
          newErrors.location = 'Location is required';
        }
      } else if (formData?.role === USER_ROLES?.CITY_OFFICIAL) {
        // For city officials, validate city, organization, department, title, and location
        if (!formData?.city) {
          newErrors.city = 'City is required';
        }
        if (!formData?.organization) {
          newErrors.organization = 'Organization is required';
        }
        if (!formData?.department) {
          newErrors.department = 'Department is required';
        }
        if (!formData?.title) {
          newErrors.title = 'Job title is required';
        }
        if (!formData?.location) {
          newErrors.location = 'Location is required';
        }
      } else if (formData?.role === USER_ROLES?.PCF_ADMIN) {
        // For PCF admin, validate organization (department), title, and location
        if (!formData?.organization) {
          newErrors.organization = 'Department is required';
        }
        if (!formData?.title) {
          newErrors.title = 'Job title is required';
        }
        if (!formData?.location) {
          newErrors.location = 'Location is required';
        }
      } else {
        // For other roles (entrepreneurs, corporate partners, community members)
        if (!formData?.organization) {
          newErrors.organization = 'Organization is required';
        }
        if (!formData?.location) {
          newErrors.location = 'Location is required';
        }
      }
    }

    if (step === 4) {
      if (!formData?.termsAccepted) {
        newErrors.termsAccepted = 'You must accept the terms and conditions';
      }
      if (!formData?.privacyAccepted) {
        newErrors.privacyAccepted = 'You must accept the privacy policy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = async () => {
    // Step 2 special handling - validate email against selected role
    if (currentStep === 2) {
      if (!validateStep(currentStep)) return;
      
      // Validate email authorization for the selected role
      const isEmailValid = await validateEmailAuthorization();
      if (!isEmailValid) return;
    } else {
      // Regular validation for other steps
      if (!validateStep(currentStep)) return;
    }

    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsLoading(true);
    setErrors(prev => ({ ...prev, submit: '' })); // Clear previous submit errors

    try {
      console.log('Starting registration with data:', {
        email: formData?.email,
        role: formData?.role,
        fullName: formData?.fullName
      });

      // Build userData object with ALL fields including city official specific ones
      const userData = {
        fullName: formData?.fullName,
        role: formData?.role,
        organization: formData?.organization,
        title: formData?.title,
        location: formData?.location,
        bio: formData?.bio,
        phone: formData?.phone,
        linkedinUrl: formData?.linkedinUrl,
        websiteUrl: formData?.websiteUrl,
        interests: formData?.interests || [],
        skills: formData?.skills || [],
        // City Official specific fields
        city: formData?.city,
        department: formData?.department,
        // Student specific fields
        university: formData?.university === 'other' ? formData?.customUniversity : formData?.university,
        major: formData?.major,
        academicYear: formData?.academicYear,
        expectedGraduation: formData?.expectedGraduation
      };

      const result = await signUp?.(
        formData?.email,
        formData?.password,
        userData
      );

      console.log('Registration result:', result);

      if (!result) {
        setErrors({ submit: 'Registration failed. Please try again.' });
        return;
      }

      const { data, error } = result;

      if (error) {
        console.error('Registration error:', error);
        
        // Handle specific error codes
        if (error?.code === 'EMAIL_NOT_AUTHORIZED') {
          setErrors({ submit: 'This email is not authorized for the selected role. Please contact your administrator.' });
        } else if (error?.code === 'AUTHORIZATION_CHECK_FAILED') {
          setErrors({ submit: 'Unable to verify email authorization. Please try again later.' });
        } else if (error?.code === 'NETWORK_ERROR') {
          setErrors({ submit: 'Network error. Please check your connection and try again.' });
        } else if (error?.code === 'weak_password') {
          setErrors({ submit: 'Password is too weak. Please choose a stronger password.' });
        } else if (error?.message?.includes('already registered')) {
          setErrors({ submit: 'An account with this email already exists. Please try signing in instead.' });
        } else {
          setErrors({ submit: error?.message || 'Registration failed. Please try again.' });
        }
        return;
      }

      // Show success message for email confirmation
      if (data?.user && !data?.user?.email_confirmed_at) {
        setErrors({ 
          submit: 'Registration successful! Please check your email to confirm your account before signing in.' 
        });
        
        // Wait a moment then redirect to login
        setTimeout(() => {
          navigate('/user-login', { 
            state: { 
              email: formData?.email,
              message: 'Please confirm your email address to complete registration.' 
            }
          });
        }, 3000);
        return;
      }

      // Navigate to appropriate dashboard based on role
      const dashboardRoutes = {
        [USER_ROLES?.PCF_ADMIN]: '/pcf-admin-dashboard',
        [USER_ROLES?.CITY_OFFICIAL]: '/city-official-dashboard',
        [USER_ROLES?.STUDENT]: '/student-dashboard',
        [USER_ROLES?.ENTREPRENEUR]: '/business-dashboard',
        [USER_ROLES?.COMMUNITY_MEMBER]: '/student-dashboard',
        [USER_ROLES?.CORPORATE_PARTNER]: '/business-dashboard'
      };

      const dashboardRoute = dashboardRoutes?.[formData?.role] || '/student-dashboard';
      navigate(dashboardRoute);

    } catch (error) {
      console.error('Unexpected registration error:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipStep = () => {
    if (currentStep === 3) {
      setCurrentStep(4);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Basic Information</h3>
              <p className="text-sm text-muted-foreground">Tell us about yourself</p>
            </div>

            <div className="space-y-4">
              <Input
                label="Full Name"
                placeholder="Enter your full name"
                value={formData?.fullName}
                onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                error={errors?.fullName}
                required
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email address"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                required
                autoComplete="email"
              />

              <div className="space-y-2">
                <div className="relative">
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData?.password}
                    onChange={(e) => handleInputChange('password', e?.target?.value)}
                    error={errors?.password}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    aria-label="Toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                  </button>
                </div>
                {formData?.password && (
                  <PasswordStrengthIndicator password={formData?.password} />
                )}
              </div>

              <div className="relative">
                <Input
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                  error={errors?.confirmPassword}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  aria-label="Toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={18} />
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <RoleSelectionCard
              selectedRole={formData?.role}
              onRoleChange={handleRoleSelect}
              authorizedRole={authorizedRole}
              error={errors?.role}
              userEmail={formData?.email}
            />

            {/* Role Validation Error */}
            {errors?.roleValidation && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} className="text-red-600" />
                  <p className="text-red-800 text-sm font-medium">{errors?.roleValidation}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <OrganizationFields
            formData={formData}
            onChange={handleInputChange}
            errors={errors}
            userRole={formData?.role}
          />
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Additional Information</h3>
              <p className="text-sm text-muted-foreground">Help others connect with you (optional)</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Bio
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={4}
                  placeholder="Tell us about your interests and expertise in smart cities..."
                  value={formData?.bio}
                  onChange={(e) => handleInputChange('bio', e?.target?.value)}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData?.bio?.length || 0}/500 characters
                </p>
              </div>

              <Input
                label="LinkedIn URL (optional)"
                placeholder="https://linkedin.com/in/yourprofile"
                value={formData?.linkedinUrl}
                onChange={(e) => handleInputChange('linkedinUrl', e?.target?.value)}
              />

              <Input
                label="Website URL (optional)"
                placeholder="https://yourwebsite.com"
                value={formData?.websiteUrl}
                onChange={(e) => handleInputChange('websiteUrl', e?.target?.value)}
              />

              {/* Terms and Privacy */}
              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={formData?.termsAccepted}
                    onChange={(e) => handleInputChange('termsAccepted', e?.target?.checked)}
                    className="mt-1 w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                    required
                  />
                  <label htmlFor="terms" className="text-sm text-foreground">
                    I agree to the{' '}
                    <a href="#" className="text-primary hover:underline">
                      Terms and Conditions
                    </a>
                    {errors?.termsAccepted && (
                      <span className="block text-xs text-error mt-1">{errors?.termsAccepted}</span>
                    )}
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={formData?.privacyAccepted}
                    onChange={(e) => handleInputChange('privacyAccepted', e?.target?.checked)}
                    className="mt-1 w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                    required
                  />
                  <label htmlFor="privacy" className="text-sm text-foreground">
                    I agree to the{' '}
                    <a href="#" className="text-primary hover:underline">
                      Privacy Policy
                    </a>
                    {errors?.privacyAccepted && (
                      <span className="block text-xs text-error mt-1">{errors?.privacyAccepted}</span>
                    )}
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="marketing"
                    checked={formData?.marketingConsent}
                    onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
                    className="mt-1 w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="marketing" className="text-sm text-muted-foreground">
                    I would like to receive updates about smart cities events, grants, and opportunities
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-lg shadow-soft"
        >
          {/* Header */}
          <div className="p-8 border-b border-border">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
                <Icon name="UserPlus" size={32} color="white" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Join Smart Cities Hub</h1>
              <p className="text-muted-foreground">Connect with innovators, cities, and opportunities</p>
            </div>
            
            <div className="mt-8">
              <ProgressIndicator 
                currentStep={currentStep} 
                totalSteps={totalSteps}
                steps={Array.from({ length: totalSteps }, (_, i) => ({ 
                  number: i + 1, 
                  title: i === 0 ? 'Basic Info' : i === 1 ? 'Role' : i === 2 ? 'Organization' : 'Additional' 
                }))}
              />
            </div>
          </div>

          {/* Form Content */}
          <div className="p-8">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>

            {/* Submit Error */}
            {errors?.submit && (
              <div className={`mt-6 border rounded-lg p-3 ${
                errors?.submit?.includes('successful') 
                  ? 'bg-green-50 border-green-200' :'bg-error/10 border-error/20'
              }`}>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={errors?.submit?.includes('successful') ? "CheckCircle" : "AlertCircle"} 
                    size={16} 
                    className={errors?.submit?.includes('successful') ? "text-green-600" : "text-error"} 
                  />
                  <span className={`text-sm ${
                    errors?.submit?.includes('successful') ? 'text-green-800' : 'text-error'
                  }`}>
                    {errors?.submit}
                  </span>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>

              <div className="flex space-x-3">
                {currentStep === 3 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSkipStep}
                  >
                    Skip
                  </Button>
                )}

                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleNext}
                    disabled={isLoading || roleValidationLoading || (currentStep === 2 && errors?.roleValidation)}
                    loading={roleValidationLoading}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    {roleValidationLoading ? 'Validating...' : 'Next'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleSubmit}
                    loading={isLoading}
                    disabled={isLoading}
                    iconName="UserCheck"
                    iconPosition="left"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 bg-muted/30 border-t border-border rounded-b-lg">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Button
                  variant="link"
                  onClick={() => navigate('/user-login')}
                  className="text-sm p-0 h-auto"
                >
                  Sign in here
                </Button>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UserRegistration;